type WindowFill = {
  time: number;
  closedPnl: number;
  notional?: number;
  px?: number;
  sz?: number;
};

export type OroTradeWindowStats = {
  pnl24h: number;
  pnl7d: number;
  pnl30d: number;
  pnl90d: number;
  volume30d: number;
  volume90d: number;
  tradeCount7d: number;
  tradeCount30d: number;
  tradeCount90d: number;
};

export type OroRealizedWindowKpi = {
  id: '24h' | '7d' | '30d' | '90d' | 'fills';
  label: string;
  value: number;
  kind: 'pnl' | 'count';
};

export function fillTimestampMs(t: WindowFill): number {
  return t.time < 1_000_000_000_000 ? t.time * 1000 : t.time;
}

export function computeTradeWindowStats(trades: WindowFill[]): OroTradeWindowStats {
  const now = Date.now();
  const d1 = now - 86_400_000;
  const d7 = now - 7 * 86_400_000;
  const d30 = now - 30 * 86_400_000;
  const d90 = now - 90 * 86_400_000;
  let pnl24h = 0;
  let pnl7d = 0;
  let pnl30d = 0;
  let pnl90d = 0;
  let volume30d = 0;
  let volume90d = 0;
  let tradeCount7d = 0;
  let tradeCount30d = 0;
  let tradeCount90d = 0;
  for (const t of trades) {
    const ts = fillTimestampMs(t);
    const pnl = t.closedPnl;
    const ntl = t.notional != null ? t.notional : Math.abs((t.px || 0) * (t.sz || 0));
    if (ts >= d90) {
      pnl90d += pnl;
      volume90d += ntl;
      tradeCount90d += 1;
    }
    if (ts >= d30) {
      pnl30d += pnl;
      volume30d += ntl;
      tradeCount30d += 1;
    }
    if (ts >= d7) {
      pnl7d += pnl;
      tradeCount7d += 1;
    }
    if (ts >= d1) pnl24h += pnl;
  }
  return {
    pnl24h,
    pnl7d,
    pnl30d,
    pnl90d,
    volume30d,
    volume90d,
    tradeCount7d,
    tradeCount30d,
    tradeCount90d,
  };
}

/**
 * Nested realized windows for the Performance tab.
 * Only emit calendar windows that fit inside the fetched lookback so 7D
 * never clones a fake 30D number, and 90D actually shows 90D realized.
 */
export function realizedWindowKpis(
  windows: OroTradeWindowStats,
  days: 7 | 30 | 90,
): OroRealizedWindowKpi[] {
  const rows: OroRealizedWindowKpi[] = [
    { id: '24h', label: '24H realized', value: windows.pnl24h, kind: 'pnl' },
  ];
  if (days >= 7) {
    rows.push({ id: '7d', label: '7D realized', value: windows.pnl7d, kind: 'pnl' });
  }
  if (days >= 30) {
    rows.push({ id: '30d', label: '30D realized', value: windows.pnl30d, kind: 'pnl' });
  }
  if (days >= 90) {
    rows.push({ id: '90d', label: '90D realized', value: windows.pnl90d, kind: 'pnl' });
  }
  const fills =
    days === 7 ? windows.tradeCount7d : days === 90 ? windows.tradeCount90d : windows.tradeCount30d;
  rows.push({ id: 'fills', label: `${days}D fills`, value: fills, kind: 'count' });
  return rows;
}
